import { useTranslation } from "react-i18next";
import { panelSm } from "@design-system";
import { isTimePriceDescription } from "../../utils/pricing";

export interface PriceAmounts {
  totalAmount: number;
  totalAmountWithoutVat: number;
  totalVatAmount: number;
}

export interface PriceItem {
  description: string;
  price: number;
  quantity: number;
  unit: string;
  vat: number;
  discountInPercent: number;
}

export interface PricingData {
  amounts: PriceAmounts;
  items: PriceItem[];
}

/** Shared presentation for calculated order prices and saved template rates. */
export const OrderPriceDetails = ({
  items,
  amounts,
  ratesOnly = false,
}: {
  items: PriceItem[];
  amounts?: PriceAmounts;
  ratesOnly?: boolean;
}) => {
  const { t } = useTranslation("common");
  return (
    <div className={`${panelSm} overflow-hidden`}>
      {/* Price Items Table */}
      <div className="overflow-x-auto">
        <table className={ratesOnly ? "w-full" : "w-full min-w-[600px]"}>
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="text-left py-3 px-2 sm:px-4 font-medium text-gray-800 dark:text-gray-200 text-xs sm:text-sm min-w-[120px]">
                {t("common:description", "Beskrivelse")}
              </th>
              {!ratesOnly && (
                <th className="text-center py-3 px-2 sm:px-4 font-medium text-gray-800 dark:text-gray-200 text-xs sm:text-sm min-w-[60px]">
                  {t("common:quantity", "Antall")}
                </th>
              )}
              <th className="text-right py-3 px-2 sm:px-4 font-medium text-gray-800 dark:text-gray-200 text-xs sm:text-sm min-w-[80px]">
                {t("common:price", "Pris")}
              </th>
              <th className="text-right py-3 px-2 sm:px-4 font-medium text-gray-800 dark:text-gray-200 text-xs sm:text-sm min-w-[60px]">
                {t("common:vat", "MVA")}
              </th>
              {!ratesOnly && (
                <th className="text-right py-3 px-2 sm:px-4 font-medium text-gray-800 dark:text-gray-200 text-xs sm:text-sm min-w-[80px]">
                  {t("common:total", "Total")}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => {
              const lineSubtotal = (item.price || 0) * (item.quantity || 1);
              const vatRateNum = (item.vat || 0) / 100;
              const lineVatAmount = lineSubtotal * vatRateNum;
              const lineTotalWithVat = lineSubtotal + lineVatAmount;
              // Time-price lines have no known quantity until the order is
              // completed. The backend returns quantity 0 and excludes them
              // from the total, so avoid fabricating a quantity of 1 (which
              // otherwise shows a phantom amount not present in the total).
              const isTimePricePending =
                isTimePriceDescription(item.description) &&
                !(typeof item.quantity === "number" && item.quantity > 0);

              return (
                <tr
                  key={index}
                  className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/30"
                >
                  <td className="py-3 px-2 sm:px-4 text-secondary font-medium text-xs sm:text-sm">
                    <div className="break-words">
                      {item.description || t("common:not_available", "N/A")}
                    </div>
                  </td>
                  {!ratesOnly && (
                    <td className="py-3 px-2 sm:px-4 text-center text-muted text-xs sm:text-sm">
                      <div className="whitespace-nowrap">
                        {item.quantity !== undefined
                          ? `${item.quantity > 0 ? item.quantity : ""} ${item.unit || ""}`
                          : "-"}
                      </div>
                    </td>
                  )}
                  <td className="py-3 px-2 sm:px-4 text-right text-secondary text-xs sm:text-sm">
                    <div className="whitespace-nowrap">
                      {(item.price || 0).toLocaleString()} NOK
                      {item.unit && (ratesOnly || item.unit !== "pcs")
                        ? ` / ${item.unit}`
                        : ""}
                    </div>
                  </td>
                  <td className="py-3 px-2 sm:px-4 text-right text-muted text-xs sm:text-sm">
                    {item.vat !== undefined && item.vat > 0
                      ? `${item.vat}%`
                      : "-"}
                  </td>
                  {!ratesOnly && (
                    <td className="py-3 px-2 sm:px-4 text-right text-secondary font-medium text-xs sm:text-sm">
                      <div className="whitespace-nowrap">
                        {isTimePricePending
                          ? t("common:calculated_label", "Blir beregnet")
                          : `${lineTotalWithVat.toLocaleString()} NOK`}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Price Summary */}
      {!ratesOnly && amounts && (
        <div className="bg-gray-50 dark:bg-gray-700 px-2 sm:px-4 py-4">
          <div className="flex justify-end">
            <div className="w-full max-w-sm">
              <div className="grid grid-cols-2 gap-x-2 sm:gap-x-4 gap-y-2 text-xs sm:text-sm">
                {/* Subtotal excluding VAT */}
                <div className="text-gray-600 dark:text-gray-300 text-right break-words">
                  {(amounts.totalVatAmount || 0) > 0
                    ? t("common:total_excl_vat", "Total eks. MVA")
                    : t("common:subtotal", "Subtotal")}
                  :
                </div>
                <div className="font-medium text-gray-800 dark:text-gray-100 text-right whitespace-nowrap">
                  {(amounts.totalAmountWithoutVat || 0).toLocaleString()} NOK
                </div>

                {/* VAT amount if present */}
                {(amounts.totalVatAmount || 0) > 0 && (
                  <>
                    <div className="text-gray-600 dark:text-gray-300 text-right">
                      {t("common:vat", "MVA")}:
                    </div>
                    <div className="font-medium text-gray-800 dark:text-gray-100 text-right whitespace-nowrap">
                      {(amounts.totalVatAmount || 0).toLocaleString()} NOK
                    </div>
                  </>
                )}

                {/* Total including VAT */}
                <div className="text-text text-right font-bold pt-2 border-t border-border text-sm sm:text-base break-words">
                  {(amounts.totalVatAmount || 0) > 0
                    ? t("common:total_incl_vat", "Total inkl. MVA")
                    : t("common:total", "Total")}
                  :
                </div>
                <div className="text-text text-right font-bold pt-2 border-t border-border text-sm sm:text-base whitespace-nowrap">
                  {(amounts.totalAmount || 0).toLocaleString()} NOK
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
